// src/components/admin/AdminOrderDetail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, Clock, User, Phone, Mail, MapPin,
  FileText, Package, DollarSign, Truck, Calendar
} from 'lucide-react';
import { supabase } from '../../services/supabase';

const STATUS_CONFIG = {
  pending:   { label: '待處理', bg: '#fef3c7', color: '#92400e' },
  preparing: { label: '備餐中', bg: '#dbeafe', color: '#1e40af' },
  ready:     { label: '已出餐', bg: '#d1fae5', color: '#065f46' },
  completed: { label: '已完成', bg: '#f1f5f9', color: '#475569' },
  cancelled: { label: '已取消', bg: '#fef2f2', color: '#b91c1c' },
};

const PAYMENT_STATUS = {
  paid:     { label: '已付款', bg: '#f0fdf4', color: '#15803d' },
  pending:  { label: '待付款', bg: '#fff7ed', color: '#c2410c' },
  refunded: { label: '已退款', bg: '#fef2f2', color: '#b91c1c' },
  failed:   { label: '付款失敗', bg: '#fef2f2', color: '#b91c1c' },
};

const DetailStyles = () => (
  <style>{`
    .od-page {
    }

    /* ===== Header ===== */
    .od-header {
      margin-bottom: 14px;
    }
    .od-order-num {
      font-family: 'SF Mono', 'Fira Code', monospace;
      font-size: 1.5rem;
      font-weight: 700;
      color: #1e293b;
    }
    .od-meta {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 10px;
      flex-wrap: wrap;
    }
    .od-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 0.78rem;
      font-weight: 700;
      padding: 4px 12px;
      border-radius: 6px;
    }
    .od-mode-badge {
      font-size: 0.75rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 6px;
      background: #f1f5f9;
      color: #475569;
    }
    .od-date {
      font-size: 0.88rem;
      color: #94a3b8;
    }

    /* ===== Grid ===== */
    .od-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 16px;
    }
    .od-two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .od-section {
      background: white;
      padding: 20px;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
    }
    .od-section-title {
      font-size: 1rem;
      font-weight: 400;
      color: #2F7D5E;
      letter-spacing: 0.3px;
      margin-bottom: 16px;
      padding-bottom: 10px;
      border-bottom: 2px solid #1e293b;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .od-section-title svg {
      color: #2F7D5E;
    }
    .od-row {
      display: flex;
      justify-content: space-between;
      padding: 7px 0;
      font-size: 0.92rem;
    }
    .od-label {
      color: var(--saas-text-primary);
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .od-value {
      color: var(--saas-text-primary);
      font-weight: 600;
      text-align: right;
    }
    .od-row.grand {
      border-top: 2px solid #1e293b;
      padding-top: 12px;
      margin-top: 8px;
      font-weight: 700;
      font-size: 1.05rem;
    }
    .od-row.grand .od-value {
      color: #1e293b;
    }

    /* ===== Items ===== */
    .od-item-list {
      display: flex;
      flex-direction: column;
    }
    .od-day-header {
      font-size: 0.9rem;
      font-weight: 700;
      color: var(--saas-text-primary);
      margin: 14px 0 6px;
      padding-bottom: 6px;
      border-bottom: 1px dashed #e2e8f0;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .od-day-header:first-child {
      margin-top: 0;
    }
    .od-item-row {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 12px 0;
      border-bottom: 1px solid #f1f5f9;
    }
    .od-item-row:last-child {
      border-bottom: none;
    }
    .od-item-image {
      width: 56px;
      height: 56px;
      border-radius: 10px;
      object-fit: cover;
      flex-shrink: 0;
      border: 1px solid #e5e7eb;
    }
    .od-item-placeholder {
      width: 56px;
      height: 56px;
      border-radius: 10px;
      background: white;
      border: 1px dashed #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.4rem;
      flex-shrink: 0;
    }
    .od-item-content {
      flex: 1;
      min-width: 0;
    }
    .od-item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2px;
    }
    .od-meal-badge {
      font-size: 0.7rem;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 4px;
    }
    .od-meal-lunch {
      background: #fef3c7;
      color: #92400e;
    }
    .od-meal-dinner {
      background: #dbeafe;
      color: #1e40af;
    }
    .od-time-badge {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      font-size: 0.7rem;
      color: #64748b;
      background: white;
      border: 1px solid #e2e8f0;
      padding: 2px 8px;
      border-radius: 4px;
      font-weight: 500;
      margin-left: 6px;
    }
    .od-item-name {
      font-weight: 600;
      font-size: 0.95rem;
      color: var(--saas-text-primary);
      margin-top: 3px;
    }
    .od-item-price {
      font-weight: 700;
      color: #1e293b;
      font-size: 0.95rem;
      white-space: nowrap;
    }
    .od-item-mods {
      margin-top: 4px;
    }
    .od-mod {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;
      font-size: 0.88rem;
      color: var(--saas-text-primary);
    }
    .od-mod-name::before {
      content: "+ ";
    }
    .od-mod-price {
      font-weight: 600;
      color: #1e293b;
    }
    .od-notes-value {
      color: var(--saas-text-primary);
      font-size: 0.92rem;
      line-height: 1.5;
    }

    /* Schedule */
    .od-schedule {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .od-schedule-row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.9rem;
      color: #475569;
    }
    .od-schedule-day {
      font-weight: 600;
      min-width: 140px;
    }

    /* Loading / Error */
    .od-loading {
      text-align: center;
      padding: 60px 20px;
      color: var(--saas-text-secondary);
    }
    .od-loading-spinner {
      animation: od-spin 1s linear infinite;
      margin: 0 auto 16px;
      display: block;
    }
    @keyframes od-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .od-error {
      text-align: center;
      padding: 60px 20px;
      color: #b91c1c;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .od-two-col {
        grid-template-columns: 1fr;
      }
      .od-section {
        padding: 16px;
      }
    }
  `}</style>
);

const AdminOrderDetail = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();
      if (fetchError) throw fetchError;
      setOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <DetailStyles />
        <div className="od-loading">
          <Loader2 size={40} className="od-loading-spinner" />
          <p>正在載入訂單...</p>
        </div>
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <DetailStyles />
        <div className="od-error">
          <p>找不到此訂單</p>
        </div>
      </>
    );
  }

  const di = order.delivery_info || {};
  const ci = order.contact_info || {};
  const statusConf = STATUS_CONFIG[order.status] || {};
  const paymentConf = PAYMENT_STATUS[order.payment_status] || {};
  const bookingMode = order.booking_mode === 'single' ? '即時單點' : '週餐訂閱';

  const getCalculatedDate = (createdAt, dayIndex) => {
    const date = new Date(createdAt);
    date.setDate(date.getDate() + parseInt(dayIndex));
    return date.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'short' });
  };

  const renderMealItem = (meal, type, timeValue) => (
    <div className="od-item-row">
      {meal.image_url ? (
        <img
          src={meal.image_url}
          alt={meal.name}
          className="od-item-image"
          onError={(e) => {
            e.target.style.display = 'none';
            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : (
        <div className="od-item-placeholder">{meal.emoji || '🍽️'}</div>
      )}
      {meal.image_url && (
        <div className="od-item-placeholder" style={{ display: 'none' }}>
          {meal.emoji || '🍽️'}
        </div>
      )}
      <div className="od-item-content">
        <div className="od-item-header">
          <span>
            <span className={`od-meal-badge od-meal-${type}`}>
              {type === 'lunch' ? '午餐' : '晚餐'}
            </span>
            {timeValue && (
              <span className="od-time-badge"><Clock size={11} /> {timeValue}</span>
            )}
          </span>
          <span className="od-item-price">${meal.price?.toFixed(2)}</span>
        </div>
        <div className="od-item-name">{meal.name}{meal.quantity > 1 ? ` x${meal.quantity}` : ''}</div>
        {meal.modifiers?.length > 0 && (
          <div className="od-item-mods">
            {meal.modifiers.map((mod, mi) => (
              <div className="od-mod" key={mi}>
                <span className="od-mod-name">{mod.name || mod.alternate_name}</span>
                {mod.price > 0 && <span className="od-mod-price">+${mod.price.toFixed(2)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderCartItems = () => {
    const cartItems = order.items_data?.cart_items;
    if (!cartItems || cartItems.length === 0) return null;
    return (
      <div className="od-item-list">
        {cartItems.map((item, idx) => (
          <div className="od-item-row" key={idx}>
            {item.image_url ? (
              <img src={item.image_url} alt={item.name} className="od-item-image" loading="lazy" decoding="async"
                onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
            ) : (
              <div className="od-item-placeholder">{item.emoji || '🍽️'}</div>
            )}
            {item.image_url && (
              <div className="od-item-placeholder" style={{ display: 'none' }}>{item.emoji || '🍽️'}</div>
            )}
            <div className="od-item-content">
              <div className="od-item-header">
                <span className="od-item-name" style={{ margin: 0 }}>{item.name} x{item.quantity || 1}</span>
                <span className="od-item-price">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
              </div>
              {item.modifiers?.length > 0 && (
                <div className="od-item-mods">
                  {item.modifiers.map((mod, mi) => (
                    <div className="od-mod" key={mi}>
                      <span className="od-mod-name">{mod.name || mod.alternate_name}</span>
                      {mod.price > 0 && <span className="od-mod-price">+${mod.price.toFixed(2)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderWeeklyItems = () => {
    if (!order.items_data) return null;
    const entries = Object.entries(order.items_data).filter(([key]) => key !== 'cart_items');
    if (entries.length === 0) return null;
    return (
      <div className="od-item-list">
        {entries.map(([dayId, dayData]) => {
          let dateDisplay = '';
          if (dayData.date_info?.full_date) {
            dateDisplay = `${dayData.date_info.full_date} (${dayData.date_info.weekday})`;
          } else {
            dateDisplay = getCalculatedDate(order.created_at, dayId);
          }
          const lunchTime = order.delivery_schedule?.[`${dayId}_lunch`];
          const dinnerTime = order.delivery_schedule?.[`${dayId}_dinner`];
          return (
            <div key={dayId}>
              <div className="od-day-header">
                <Calendar size={14} /> {dateDisplay}
              </div>
              {dayData.lunch && renderMealItem(dayData.lunch, 'lunch', lunchTime)}
              {dayData.dinner && renderMealItem(dayData.dinner, 'dinner', dinnerTime)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <DetailStyles />
      <div className="od-page">
        {/* Header: order number + badges */}
        <div className="od-header">
          <span className="od-order-num">#{order.order_number}</span>
        </div>

        {(di.order_type || di.fulfillment_mode) && (
          <div className="od-section" style={{ marginBottom: 20 }}>
            <div className="od-section-title"><Package size={18} /> 訂單類型</div>
            {di.order_type && (
              <div className="od-row">
                <span className="od-label">取餐方式</span>
                <span className="od-value">{di.order_type === 'delivery' ? '配送' : '自取'}</span>
              </div>
            )}
            {di.fulfillment_mode && (
              <div className="od-row">
                <span className="od-label">取餐類型</span>
                <span className="od-value">{di.fulfillment_mode === 'instant' ? '立即配送' : '預約時間取餐'}</span>
              </div>
            )}
          </div>
        )}

        <div className="od-grid">
          {/* Row 1: Contact + Delivery side by side */}
          <div className="od-two-col">
            <div className="od-section">
              <div className="od-section-title"><User size={18} /> 聯絡資料</div>
              <div className="od-row">
                <span className="od-label"><User size={13} /> 姓名</span>
                <span className="od-value">{ci.name || '-'}</span>
              </div>
              <div className="od-row">
                <span className="od-label"><Phone size={13} /> 電話</span>
                <span className="od-value">{ci.phone || '-'}</span>
              </div>
              <div className="od-row">
                <span className="od-label"><Mail size={13} /> Email</span>
                <span className="od-value">{ci.email || '-'}</span>
              </div>
            </div>

            <div className="od-section">
              <div className="od-section-title"><Truck size={18} /> 取餐資訊</div>
              {di.address && (
                <div className="od-row">
                  <span className="od-label"><MapPin size={13} /> 地址</span>
                  <span className="od-value">{di.address}{di.unit ? ` ${di.unit}` : ''}</span>
                </div>
              )}
              {di.city && (
                <div className="od-row">
                  <span className="od-label">城市</span>
                  <span className="od-value">{di.city}</span>
                </div>
              )}
              {di.postal_code && (
                <div className="od-row">
                  <span className="od-label">郵遞區號</span>
                  <span className="od-value">{di.postal_code}</span>
                </div>
              )}
              {di.zone_name && (
                <div className="od-row">
                  <span className="od-label">配送區域</span>
                  <span className="od-value">{di.zone_name}</span>
                </div>
              )}
              {di.scheduled_time && (
                <div className="od-row">
                  <span className="od-label">預約時間</span>
                  <span className="od-value">{di.scheduled_time}</span>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Order items + Fee side by side */}
          <div className="od-two-col">
            <div className="od-section">
              <div className="od-section-title"><Package size={18} /> 訂單內容</div>
              {order.booking_mode === 'single' ? renderCartItems() : renderWeeklyItems()}
            </div>

            <div className="od-section">
              <div className="od-section-title"><DollarSign size={18} /> 費用明細</div>
              {(order.discount > 0 || order.totals_data?.discount > 0) ? (
                <>
                  <div className="od-row">
                    <span className="od-label">原價小計</span>
                    <span className="od-value" style={{textDecoration:'line-through', color:'#94a3b8'}}>${order.totals_data?.original_subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="od-row" style={{color:'#e53e3e'}}>
                    <span className="od-label">{order.totals_data?.discount_label || '全單折扣'}{order.totals_data?.discount_rate ? ` (${Math.round(order.totals_data.discount_rate * 100)}折)` : ''}</span>
                    <span className="od-value">-${(order.discount || order.totals_data?.discount || 0).toFixed(2)}</span>
                  </div>
                  <div className="od-row">
                    <span className="od-label">折後小計</span>
                    <span className="od-value">${order.subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                </>
              ) : (
                <div className="od-row">
                  <span className="od-label">小計</span>
                  <span className="od-value">${order.subtotal?.toFixed(2) || '0.00'}</span>
                </div>
              )}
              <div className="od-row">
                <span className="od-label">配送費</span>
                <span className="od-value">{order.delivery_fee === 0 ? '免費' : `$${order.delivery_fee?.toFixed(2) || '0.00'}`}</span>
              </div>
              <div className="od-row">
                <span className="od-label">GST (5%)</span>
                <span className="od-value">${order.gst?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="od-row">
                <span className="od-label">PST (7%)</span>
                <span className="od-value">${order.pst?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="od-row grand">
                <span className="od-label">總計</span>
                <span className="od-value">${order.total_amount?.toFixed(2) || '0.00'}</span>
              </div>

              {/* Notes inline if present */}
              {(ci.notes || order.notes) && (
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontWeight: 600, fontSize: '0.9rem', color: 'var(--saas-text-primary)' }}>
                    <FileText size={13} /> 備註
                  </div>
                  <div className="od-notes-value">{ci.notes || order.notes}</div>
                </div>
              )}
            </div>
          </div>

          {/* Row 3: Delivery schedule (weekly only) */}
          {order.delivery_schedule && Object.keys(order.delivery_schedule).length > 0 && (
            <div className="od-section">
              <div className="od-section-title"><Calendar size={18} /> 配送時間表</div>
              <div className="od-schedule">
                {Object.entries(order.delivery_schedule).map(([key, time]) => {
                  const [dayId, mealType] = key.split('_');
                  const dayData = order.items_data?.[dayId];
                  const dateStr = dayData?.date_info?.full_date || `第 ${parseInt(dayId) + 1} 天`;
                  const mealLabel = mealType === 'lunch' ? '午餐' : '晚餐';
                  return (
                    <div className="od-schedule-row" key={key}>
                      <span className="od-schedule-day">{dateStr} {mealLabel}</span>
                      <Clock size={13} />
                      <span>{time}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminOrderDetail;
